import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { inr } from "@/lib/api";
import { Wallet as WalletIcon, Sparkles, ArrowUpRight, ArrowDownLeft, ShieldCheck, CreditCard } from "lucide-react";
import { fmtDateTime12 } from "@/lib/time";

export default function CustomerWallet() {
  const [w, setW] = useState(null);

  useEffect(() => {
    api.get("/wallet/mine").then((r) => setW(r.data)).catch(() => setW({ balance: 0, transactions: [] }));
  }, []);

  if (!w) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="premium-spinner mb-3"></div>
        <div className="text-xs text-amber-400 font-bold uppercase tracking-widest animate-pulse">Loading Wallet Details...</div>
      </div>
    );
  }

  return (
    <div data-testid="customer-wallet" className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="border-b-2 border-white/10 pb-6"
      >
        <div className="text-xs uppercase tracking-[0.25em] text-emerald-400 font-extrabold mb-1 flex items-center gap-2">
          <CreditCard size={16} /> CLIENT REWARDS & CREDIT WALLET
        </div>
        <h1 className="text-4xl lg:text-5xl font-heading text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-hgold to-emerald-400 font-bold">
          My Wallet
        </h1>
      </motion.div>

      {/* Wallet Cards Grid */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="grid grid-cols-1 lg:grid-cols-3 gap-6"
      >
        {/* Main Balance Card */}
        <div
          className="lg:col-span-2 rounded-2xl border border-emerald-400/80 p-8 bg-gradient-to-b from-emerald-950/30 via-black/90 to-emerald-950/20 shadow-2xl shadow-emerald-950/30 flex flex-col justify-between space-y-6"
          data-testid="wallet-balance-card"
        >
          <div className="flex justify-between items-start">
            <div>
              <div className="text-xs uppercase tracking-widest text-emerald-400 font-extrabold mb-2">Available Wallet Credits</div>
              <div className="font-heading text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 via-teal-200 to-white">
                {inr(w.balance || 0)}
              </div>
            </div>
            <div className="p-4 rounded-2xl border border-emerald-400/40 bg-emerald-950/40">
              <WalletIcon size={36} className="text-emerald-400" />
            </div>
          </div>

          <div className="flex flex-wrap gap-8 pt-6 border-t border-white/10 text-xs">
            <div>
              <div className="text-[10px] font-extrabold uppercase tracking-widest text-white/40 mb-1">Active Membership</div>
              <div className="font-bold text-amber-300 text-sm flex items-center gap-1.5">
                <ShieldCheck size={16} className="text-emerald-400" />
                {w.membership_name || "No Membership Tier"}
              </div>
            </div>
            {w.expires_at && (
              <div>
                <div className="text-[10px] font-extrabold uppercase tracking-widest text-white/40 mb-1">Validity Expires</div>
                <div className="font-bold text-white text-sm font-heading">
                  {(w.expires_at || "").slice(0, 10)}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Top Up Promo Box */}
        <div className="rounded-2xl border border-amber-400/70 p-6 lg:p-8 bg-gradient-to-b from-hpaper via-black/90 to-hpaper shadow-xl shadow-yellow-950/20 flex flex-col justify-between space-y-4">
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-amber-400 font-extrabold text-xs uppercase tracking-widest">
              <Sparkles size={18} /> Top Up Wallet
            </div>
            <h3 className="font-heading text-2xl font-bold text-white">Need More Credits?</h3>
            <p className="text-xs text-white/70 leading-relaxed font-medium">
              Purchase any of our luxury membership plans to receive instant extra bonus wallet credits usable across all branches and doorstep services.
            </p>
          </div>

          <Link
            to="/membership"
            data-testid="wallet-topup-btn"
            className="gold-btn w-full justify-center py-3.5 rounded-xl font-extrabold text-xs uppercase tracking-wider shadow-lg shadow-yellow-950/40"
          >
            Explore Memberships
          </Link>
        </div>
      </motion.div>

      {/* Transaction History Section */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9 }}
        className="rounded-2xl border border-white/15 overflow-hidden bg-gradient-to-b from-hpaper via-black/90 to-hpaper shadow-xl"
      >
        <div className="p-5 border-b border-white/10 text-xs font-extrabold uppercase tracking-widest text-amber-400 flex items-center justify-between">
          <span>TRANSACTION HISTORY</span>
          <span className="text-white/40">{(w.transactions || []).length} Entries</span>
        </div>

        <div className="divide-y divide-white/5">
          {(w.transactions || []).map((t) => {
            const isDeduction = t.type === "deduction" || t.type === "adjust_deduct";
            return (
              <div
                key={t.id}
                data-testid={`wallet-txn-${t.id}`}
                className="flex items-center justify-between p-5 hover:bg-white/[0.02] transition-all"
              >
                <div className="flex items-center gap-4">
                  <div
                    className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 border ${
                      isDeduction
                        ? "border-red-500/40 bg-red-950/30 text-red-400"
                        : "border-emerald-400/40 bg-emerald-950/30 text-emerald-400"
                    }`}
                  >
                    {isDeduction ? <ArrowUpRight size={18} /> : <ArrowDownLeft size={18} />}
                  </div>
                  <div>
                    <div className="text-white text-sm font-bold">{t.notes}</div>
                    <div className="text-xs text-white/50 font-medium mt-0.5">{fmtDateTime12(t.created_at)}</div>
                  </div>
                </div>

                <div className={`font-heading text-lg font-bold ${isDeduction ? "text-red-400" : "text-emerald-400"}`}>
                  {isDeduction ? "-" : "+"}{inr(t.amount)}
                </div>
              </div>
            );
          })}

          {(w.transactions || []).length === 0 && (
            <div className="text-center py-16 text-white/50 text-sm font-medium">
              No transactions yet. Purchase a membership plan to activate your wallet balance.
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
